import {useEffect, useMemo, useState} from "react";
import SiteHeader from "../components/SiteHeader.jsx";
import SiteFooter from "../components/SiteFooter.jsx";
import {fetchMsWorkSummary} from "../rest.js";

const EMPTY_NUMBER = "(n/a)"
const EMPTY_TEXT = "(none)"

const ColorPct = ({value}) => {
    if (typeof value !== "number") {
        return EMPTY_NUMBER
    }
    let cls = "font-semibold text-green-600"
    if (value >= 100) cls = "font-bold text-green-700"
    else if (value < 50) cls = "font-semibold text-amber-600"

    return <span className={cls}>{value}%</span>
}

const FinalOutputCell = ({expected, completed, pct}) =>
    <div className="flex items-baseline justify-end gap-2">
        <span className="text-slate-500 whitespace-nowrap">
            {expected == null || completed == null
                ? EMPTY_NUMBER
                : `${completed} / ${expected}`}
        </span>
        <ColorPct value={pct}/>
    </div>

const SortButton = ({active, dir, onClick}) =>
    <button
        onClick={onClick}
        className={`inline-flex items-center gap-0.5 select-none ${
            active ? "text-blue-700" : "text-slate-500 hover:text-blue-600"
        }`}
    >
        <span className="flex flex-col leading-none">
            <span className={`text-[0.55rem] ${active && dir === "asc" ? "opacity-100" : "opacity-25"}`}>▲</span>
            <span className={`text-[0.55rem] -mt-0.5 ${active && dir === "desc" ? "opacity-100" : "opacity-25"}`}>▼</span>
        </span>
    </button>

const DataSourcesTabButton = ({isActive, onClick, children}) =>
    <button
        type="button"
        className={`rounded-t border border-b-0 px-4 py-2 text-sm font-medium ${isActive ? "border-slate-300 bg-white text-slate-900" : "border-transparent bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
        onClick={onClick}
    >
        {children}
    </button>

const RiboCryptPlaceholder = () =>
    <div className="rounded border border-slate-200 bg-white shadow-sm p-8 text-center">
        <h3 className="text-lg font-semibold text-slate-700">RiboCrypt datasets</h3>
        <p className="mt-2 text-sm text-slate-500">
            Datasets re-analysed for the RiboCrypt component will be listed here.
        </p>
    </div>

const SortableHeader = ({label, sortKey, sort, onSort, onInfo, infoKey, className = ""}) =>
    <th className={`px-3 py-2 border-b border-slate-300 ${className}`}>
        <span className="inline-flex items-center gap-1">
            {
                infoKey
                    ? <button
                        onClick={() => onInfo(infoKey)}
                        title="Click for details"
                        className="inline-flex items-center gap-1 hover:text-blue-700"
                    >
                        <span>{label}</span>
                        <span className="text-[0.65rem] font-normal normal-case text-slate-400">ⓘ</span>
                    </button>
                    : <span>{label}</span>
            }
            <SortButton
                active={sort.key === sortKey}
                dir={sort.dir}
                onClick={() => onSort(sortKey)}
            />
        </span>
    </th>

const getInstrumentText = row =>
    row.instruments && row.instruments.length ? row.instruments.join(", ") : EMPTY_TEXT

const cellValue = (row, key) => {
    switch (key) {
        case "study": return row.study
        case "instruments": return getInstrumentText(row).toLowerCase()
        case "publication": return `${row.publication_title || ""} ${row.first_author || ""}`.toLowerCase()
        case "cs": return row.cs_pct ?? -1
        case "dmo": return row.dmo_pct ?? -1
        default: return ""
    }
}

const compare = (a, b, key) => {
    const va = cellValue(a, key)
    const vb = cellValue(b, key)
    if (typeof va === "number" && typeof vb === "number") {
        return va - vb
    }
    return String(va).localeCompare(String(vb))
}

const COLUMN_INFO = {
    cs: {
        title: "Closed search",
        body:
            <>
                <p>
                    Each number reports how many of the study's experiment files were
                    successfully re-analysed and inserted into OpenProt out of the total
                    number of files present (completed / total).
                </p>
                <p className="mt-2">
                    The closed search is a standard peptide search performed with SearchGUI
                    (MS-GF+, X!Tandem and Comet) and re-scored with Ms2Rescore/Percolator,
                    against the OpenProt protein library.
                </p>
            </>,
    },
    dmo: {
        title: "DMO",
        body:
            <>
                <p>
                    Each number reports how many of the study's experiment files were
                    successfully re-analysed and inserted into OpenProt out of the total
                    number of files present (completed / total).
                </p>
                <p className="mt-2">
                    The DMO (detailed mass offset) search uses MSFragger with UniMod
                    modifications to detect unanticipated post-translational modifications.
                </p>
            </>,
    },
}

const InfoModal = ({infoKey, onClose}) => {
    useEffect(() => {
        if (!infoKey) return
        const onKeyDown = e => {
            if (e.key === "Escape") onClose()
        }
        window.addEventListener("keydown", onKeyDown)
        return () => window.removeEventListener("keydown", onKeyDown)
    }, [infoKey, onClose])

    if (!infoKey) return null

    const info = COLUMN_INFO[infoKey]
    if (!info) return null

    return <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
        onClick={onClose}
    >
        <div
            className="w-full max-w-lg rounded bg-white shadow-xl border border-slate-200"
            onClick={e => e.stopPropagation()}
        >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-blue-800">{info.title}</h3>
                <button
                    onClick={onClose}
                    className="text-slate-400 hover:text-slate-700 text-lg leading-none"
                    aria-label="Close"
                >
                    ×
                </button>
            </div>
            <div className="px-4 py-3 text-sm text-gray-700 leading-relaxed">
                {info.body}
            </div>
        </div>
    </div>
}

export default () => {

    const [data, setData] = useState(null)
    const [error, setError] = useState(null)
    const [sort, setSort] = useState({key: "study", dir: "asc"})
    const [infoKey, setInfoKey] = useState(null)
    const [activeTab, setActiveTab] = useState("ms")

    useEffect(() => {
        fetchMsWorkSummary()
            .then(res => {
                if (res.error || !res.rows) {
                    throw new Error(res.error || "malformed response")
                }
                setData(res.rows)
            })
            .catch(e => setError(String(e.message || e)))
    }, [])

    const onSort = key => {
        setSort(prev => prev.key === key
            ? {key, dir: prev.dir === "asc" ? "desc" : "asc"}
            : {key, dir: key === "study" || key === "instruments" || key === "publication" ? "asc" : "desc"})
    }

    const sortedRows = useMemo(() => {
        if (!data) return []
        const dir = sort.dir === "asc" ? 1 : -1
        return [...data].sort((a, b) => compare(a, b, sort.key) * dir)
    }, [data, sort])

    return <div className="flex flex-col min-h-screen bg-slate-50">
        <SiteHeader/>

        <div className="max-w-6xl w-full mx-auto px-4 py-6 flex-1">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-blue-800">Data sources</h1>
                <p className="text-sm text-gray-500 mt-1">
                    Datasets re-analysed for OpenProt.
                </p>
            </div>

            <div className="mb-4 flex gap-2">
                <DataSourcesTabButton
                    isActive={activeTab === "ms"}
                    onClick={() => setActiveTab("ms")}
                >
                    Mass spectrometry
                </DataSourcesTabButton>
                <DataSourcesTabButton
                    isActive={activeTab === "ribocrypt"}
                    onClick={() => setActiveTab("ribocrypt")}
                >
                    RiboCrypt
                </DataSourcesTabButton>
            </div>

            {
                activeTab === "ribocrypt" &&
                <RiboCryptPlaceholder/>
            }

            {
                activeTab === "ms" &&
                <div>
                {
                    error &&
                    <div className="rounded bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                        {error}
                    </div>
                }

                {
                    !data && !error &&
                    <div className="text-sm text-gray-500">Loading…</div>
                }

                {
                    data && data.length === 0 &&
                    <div className="text-sm text-gray-500">No data sources available.</div>
                }
                </div>
            }

            {
                activeTab === "ms" &&
                sortedRows.length > 0 &&
                <div className="rounded border border-slate-200 bg-white shadow-sm overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-700 border-collapse">
                        <thead>
                            <tr className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                                <SortableHeader
                                    label="Study"
                                    sortKey="study"
                                    sort={sort}
                                    onSort={onSort}
                                    className="text-left"
                                />
                                <SortableHeader
                                    label="Instrument"
                                    sortKey="instruments"
                                    sort={sort}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Publication"
                                    sortKey="publication"
                                    sort={sort}
                                    onSort={onSort}
                                />
                                <SortableHeader
                                    label="Closed search"
                                    sortKey="cs"
                                    sort={sort}
                                    onSort={onSort}
                                    onInfo={setInfoKey}
                                    infoKey="cs"
                                    className="text-right"
                                />
                                <SortableHeader
                                    label="DMO"
                                    sortKey="dmo"
                                    sort={sort}
                                    onSort={onSort}
                                    onInfo={setInfoKey}
                                    infoKey="dmo"
                                    className="text-right"
                                />
                            </tr>
                        </thead>
                        <tbody>
                            {
                                sortedRows.map((row, idx) => (
                                    <tr key={idx} className="even:bg-slate-50 hover:bg-blue-50/50">
                                        <td className="px-3 py-2 border-b border-slate-200 font-mono text-xs whitespace-nowrap">
                                            {
                                                row.source_url
                                                    ? <a
                                                        href={row.source_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-700 underline hover:font-semibold"
                                                    >
                                                        {row.study}
                                                    </a>
                                                    : row.study
                                            }
                                        </td>
                                        <td className="px-3 py-2 border-b border-slate-200 text-slate-700">
                                            {row.instruments && row.instruments.length
                                                ? row.instruments.join(", ")
                                                : <span className="text-slate-400">{EMPTY_TEXT}</span>}
                                        </td>
                                        <td className="px-3 py-2 border-b border-slate-200 text-slate-700">
                                            {
                                                row.publication_title
                                                    ? <span>
                                                        {row.publication_title}
                                                        {
                                                            row.first_author &&
                                                            <span className="ml-1 text-xs text-slate-400 italic">
                                                                ({row.first_author} et al.)
                                                            </span>
                                                        }
                                                    </span>
                                                    : <span className="text-slate-400">{EMPTY_TEXT}</span>
                                            }
                                        </td>
                                        <td className="px-3 py-2 border-b border-slate-200">
                                            <FinalOutputCell
                                                expected={row.cs_total}
                                                completed={row.cs_completed}
                                                pct={row.cs_pct}
                                            />
                                        </td>
                                        <td className="px-3 py-2 border-b border-slate-200">
                                            <FinalOutputCell
                                                expected={row.dmo_total}
                                                completed={row.dmo_completed}
                                                pct={row.dmo_pct}
                                            />
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            }
        </div>

        <InfoModal infoKey={infoKey} onClose={() => setInfoKey(null)}/>

        <SiteFooter/>
    </div>
}
